import React, { useState, useEffect } from 'react';
import { authApi } from '@/services/api';

interface AuthDiagnostics {
    authentication: {
        isAuthenticated: boolean;
        authType: string;
        claimsCount: number;
        hasCookie: boolean;
        cookieScheme: string;
    };
    claims: Record<string, string>;
    authResult: {
        succeeded: boolean;
        principal: string;
        properties: Record<string, string>;
    } | null;
    request: {
        scheme: string;
        host: string;
        isHttps: boolean;
        userAgent: string;
        cookies: string[];
    };
    server: {
        environment: string;
        timestamp: string;
    };
}

export const AuthDiagnosticsPanel: React.FC<{ 
    show?: boolean;
    onClose?: () => void;
}> = ({ 
    show = false, 
    onClose 
}) => {
    const [diagnostics, setDiagnostics] = useState<AuthDiagnostics | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [dumpResult, setDumpResult] = useState<any | null>(null);

    const fetchDiagnostics = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/api/auth/auth-diagnostics', {
                credentials: 'include'
            });
            
            if (response.ok) {
                const data = await response.json();
                setDiagnostics(data);
            } else {
                setError(`HTTP ${response.status}: ${response.statusText}`);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    const testAuthEndpoints = async () => {
        try {
            
            // Test /me endpoint
            try {
                await authApi.getCurrentUser();
            } catch {
            }

            // Test auth-status endpoint
            try {
                await authApi.getAuthStatus();
            } catch {
            }

        } catch {
        }
    };

    const clearAllAuth = () => {
        // Clear localStorage
        localStorage.clear();
        
        // Clear sessionStorage
        sessionStorage.clear();
        
        // Clear cookies
        document.cookie.split(";").forEach((c) => {
            const eqPos = c.indexOf("=");
            const name = eqPos > -1 ? c.substring(0, eqPos) : c;
            document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        });
        
        fetchDiagnostics();
    };

    const runAutoAuthDump = async () => {
        setLoading(true);
        setError(null);
        setDumpResult(null);
        try {
            // Choose a test email - prefer currently selected or any from global window test users
            const email = (window as any).__DEV_TEST_EMAIL__ || (diagnostics?.request?.cookies?.length ? diagnostics.request.cookies[0] : undefined) || undefined;
            if (!email) {
                // fallback interactive prompt for convenience in dev
                const promptEmail = window.prompt("Enter @testsep.com email for auto-auth dump:", "testuser@testsep.com");
                if (!promptEmail) throw new Error("Email required");
                const res = await (window as any).authApi.autoAuthenticateDump(promptEmail);
                setDumpResult(res);
            } else {
                const res = await (window as any).authApi.autoAuthenticateDump(email);
                setDumpResult(res);
            }
        } catch (err: any) {
            setError(err?.message || String(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            fetchDiagnostics();
        }
    }, [show]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (autoRefresh && show) {
            interval = setInterval(fetchDiagnostics, 2000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [autoRefresh, show]);

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Authentication Diagnostics</h2>
                    <div className="flex gap-2">
                        <label className="flex items-center gap-1 text-sm">
                            <input
                                type="checkbox"
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                            />
                            Auto-refresh
                        </label>
                        <button
                            onClick={fetchDiagnostics}
                            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                            disabled={loading}
                        >
                            {loading ? 'Loading...' : 'Refresh'}
                        </button>
                        <button
                            onClick={testAuthEndpoints}
                            className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                        >
                            Test Endpoints
                        </button>
                        <button
                            onClick={runAutoAuthDump}
                            className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700"
                            disabled={loading}
                        >
                            {loading ? 'Running...' : 'Auto-Auth Dump'}
                        </button>
                        <button
                            onClick={clearAllAuth}
                            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                        >
                            Clear Auth
                        </button>
                        <button
                            onClick={onClose}
                            className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                        >
                            Close
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        <strong>Error:</strong> {error}
                    </div>
                )}

                {diagnostics && (
                    <div className="space-y-4 text-sm">
                        {/* Authentication Status */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded">
                            <h3 className="font-semibold mb-2 flex items-center gap-2">
                                Authentication Status
                                <div className={`w-2 h-2 rounded-full ${diagnostics.authentication.isAuthenticated ? 'bg-green-500' : 'bg-red-500'}`} />
                            </h3>
                            <div className="grid grid-cols-2 gap-2">
                                <div>Authenticated: {diagnostics.authentication.isAuthenticated ? 'Yes' : 'No'}</div>
                                <div>Auth Type: {diagnostics.authentication.authType || 'None'}</div>
                                <div>Claims Count: {diagnostics.authentication.claimsCount}</div>
                                <div>Has Cookie: {diagnostics.authentication.hasCookie ? 'Yes' : 'No'}</div>
                            </div>
                        </div>

                        {/* Claims */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded">
                            <h3 className="font-semibold mb-2">Claims</h3>
                            {Object.keys(diagnostics.claims).length > 0 ? (
                                <div className="space-y-1">
                                    {Object.entries(diagnostics.claims).map(([key, value]) => (
                                        <div key={key} className="grid grid-cols-3 gap-2">
                                            <div className="font-mono text-xs">{key}:</div>
                                            <div className="col-span-2 font-mono text-xs break-all">{value}</div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-gray-500">No claims found</div>
                            )}
                        </div>

                        {/* Request Info */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded">
                            <h3 className="font-semibold mb-2">Request Information</h3>
                            <div className="grid grid-cols-2 gap-2">
                                <div>Scheme: {diagnostics.request.scheme}</div>
                                <div>Host: {diagnostics.request.host}</div>
                                <div>HTTPS: {diagnostics.request.isHttps ? 'Yes' : 'No'}</div>
                                <div>Cookies: {diagnostics.request.cookies.length}</div>
                            </div>
                            {diagnostics.request.cookies.length > 0 && (
                                <div className="mt-2">
                                    <div className="text-xs text-gray-600">Cookie Names:</div>
                                    <div className="text-xs font-mono">{diagnostics.request.cookies.join(', ')}</div>
                                </div>
                            )}
                        </div>

                        {/* Auth Result */}
                        {diagnostics.authResult && (
                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded">
                                <h3 className="font-semibold mb-2">Authentication Result</h3>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>Succeeded: {diagnostics.authResult.succeeded ? 'Yes' : 'No'}</div>
                                    <div>Principal: {diagnostics.authResult.principal || 'None'}</div>
                                </div>
                                {diagnostics.authResult.properties && (
                                    <div className="mt-2">
                                        <div className="text-xs text-gray-600">Properties:</div>
                                        <div className="text-xs font-mono">
                                            {JSON.stringify(diagnostics.authResult.properties, null, 2)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Browser Info */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded">
                            <h3 className="font-semibold mb-2">Browser Information</h3>
                            <div className="space-y-1 text-xs">
                                <div>URL: {window.location.href}</div>
                                <div>Protocol: {window.location.protocol}</div>
                                <div>Hostname: {window.location.hostname}</div>
                                <div>Port: {window.location.port}</div>
                                <div>User Agent: {navigator.userAgent}</div>
                            </div>
                        </div>
                    </div>
                )}

                {dumpResult && (
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded mt-4">
                        <h3 className="font-semibold mb-2">Auto-Auth Dump Result</h3>
                        <div className="text-xs font-mono break-all">
                            <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(dumpResult, null, 2)}</pre>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuthDiagnosticsPanel;